import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
  type MotionValue,
  type Transition,
} from 'framer-motion';
import { fanPose, fanUnit, restPose } from '@/lib/folderPreview';

export interface FolderFloatEntry {
  /** Stable id, handed back to onSelect. */
  key: string;
  /** Plain text: the paper's fallback content and its accessible name. */
  label: string;
  /** Rich preview drawn on the paper. Falls back to the label. */
  content?: React.ReactNode;
}

/** Where a paper was dropped, in screen pixels, with the folder's box to map it. */
export interface FolderDrop {
  clientX: number;
  clientY: number;
  folderRect: DOMRect;
}

/** A press on a paper that may turn into a drag. */
interface PaperPress {
  key: string;
  index: number;
  pointerId: number;
  startX: number;
  startY: number;
  el: HTMLElement;
  active: boolean;
  /** Pointer offset from the ghost's top-left, screen px. */
  offsetX: number;
  offsetY: number;
}

/** Screen px a press must travel before it counts as a drag, not a click. */
const DRAG_THRESHOLD = 5;

export interface FolderFloatProps {
  items: FolderFloatEntry[];
  label: string;
  sublabel?: string;
  /** hover: fan out while the pointer is over the folder. click: toggle. */
  trigger?: 'hover' | 'click';
  closeOnSelect?: boolean;
  /** Papers lean toward the pointer on a spring. */
  physics?: boolean;
  /** 0..1, how far the papers lean. */
  drift?: number;
  onSelect?: (key: string, index: number) => void;
  onOpenChange?: (open: boolean) => void;
  /**
   * Makes papers draggable. Called when a paper is dropped outside the folder;
   * resolve false to send it back. Without it, papers only click.
   */
  onDragOut?: (key: string, index: number, drop: FolderDrop) => boolean | Promise<boolean>;
  /** Keep the folder shut (dragging, resizing, panning). */
  disabled?: boolean;
  /** Open the flap without fanning the papers: a drop target being hovered. */
  flapOpen?: boolean;

  folderColor?: string;
  frontColor?: string;
  paperColor?: string;
  itemColor?: string;
  itemTextColor?: string;
  labelColor?: string;
  /** Selection stroke around the folder silhouette. */
  outlineColor?: string | null;

  width?: number;
  height?: number;
  radius?: number;
  /** Widest distance between the outer papers' centers, px. */
  spread?: number;
  /** How far the fan rises above the folder, px. */
  lift?: number;
  /** Rotation of the outer papers, degrees. */
  tilt?: number;
  /** Flap angle while open, degrees. */
  flapAngle?: number;
  /** Flap angle at rest, degrees: enough to show the papers' tops. */
  restAngle?: number;
  /** Spring duration of the open/close, ms. */
  openDuration?: number;
  /** Delay between papers, ms. */
  stagger?: number;
  /** Spring bounce, 0..1. */
  bounce?: number;

  /** Hover time before opening, ms. Stops a pointer crossing the canvas from popping every folder. */
  openDelay?: number;
  /** Delay before reopening right after `disabled` clears, ms (outlasts a double-click). */
  reopenDelay?: number;
  /** Grace period after the pointer leaves, ms. */
  closeDelay?: number;

  /** Overlays drawn above the folder (drop progress and the like). */
  children?: React.ReactNode;
  className?: string;
}

/** Brand spring for anything that follows the pointer. */
const FOLLOW_SPRING = { stiffness: 500, damping: 38 };
const STILL: Transition = { duration: 0 };

const clampUnit = (v: number) => Math.max(-1, Math.min(1, v));

/**
 * A folder that holds papers. At rest the papers peek over a slightly tilted
 * front flap; on hover the flap drops and the papers fan out above it, each
 * one clickable. Generic: the board card feeds it item previews.
 */
export function FolderFloat({
  items,
  label,
  sublabel,
  trigger = 'hover',
  closeOnSelect = true,
  physics = true,
  drift = 0.5,
  onSelect,
  onOpenChange,
  onDragOut,
  disabled = false,
  flapOpen = false,
  folderColor = '#3f3f46',
  frontColor = '#52525b',
  paperColor = '#f5f5f5',
  itemColor = '#f5f5f5',
  itemTextColor = '#18181b',
  labelColor = '#f5f5f5',
  outlineColor = null,
  width = 200,
  height = 148,
  radius = 14,
  spread = 180,
  lift = 26,
  tilt = 8,
  flapAngle = 34,
  restAngle = 16,
  openDuration = 520,
  stagger = 45,
  bounce = 0.3,
  openDelay = 0,
  reopenDelay = 0,
  closeDelay = 120,
  children,
  className,
}: FolderFloatProps) {
  const reduced = useReducedMotion() ?? false;
  const rootRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [open, setOpen] = useState(false);
  // Closed by a pick or Escape: stays shut until the pointer leaves
  const [dismissed, setDismissed] = useState(false);
  const disabledChangedAt = useRef(0);

  const s = width / 200;
  const paperWidth = Math.round(width * 0.34);
  const paperHeight = Math.round(paperWidth * 1.25);
  const bodyTop = height * 0.1;
  const frontTop = height * 0.36;
  const paperTop = height * 0.16;
  const tabWidth = width * 0.38;
  const r = Math.min(radius, height * 0.2);

  // Pointer position over the folder, -1..1 on each axis, smoothed by a spring
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const leanX = useSpring(pointerX, FOLLOW_SPRING);
  const leanY = useSpring(pointerY, FOLLOW_SPRING);

  // Dragging a paper out: the press, and the ghost that follows the pointer
  const pressRef = useRef<PaperPress | null>(null);
  const suppressClickRef = useRef(false);
  const [ghost, setGhost] = useState<{ key: string; scale: number } | null>(null);
  const ghostX = useMotionValue(0);
  const ghostY = useMotionValue(0);
  // Faded over the folder, where letting go puts it back
  const ghostOpacity = useMotionValue(1);
  const dragging = ghost !== null;

  useEffect(() => {
    disabledChangedAt.current = Date.now();
  }, [disabled]);

  // Hover trigger: open after openDelay, close after closeDelay
  useEffect(() => {
    if (disabled) {
      setOpen(false);
      return;
    }
    if (trigger !== 'hover') return;
    const want = (hovered || dragging) && !dismissed;
    if (want === open) return;
    const justEnabled = Date.now() - disabledChangedAt.current < 600;
    const delay = want ? (justEnabled ? Math.max(openDelay, reopenDelay) : openDelay) : closeDelay;
    if (delay <= 0) {
      setOpen(want);
      return;
    }
    const t = window.setTimeout(() => setOpen(want), delay);
    return () => window.clearTimeout(t);
  }, [trigger, hovered, dragging, dismissed, disabled, open, openDelay, reopenDelay, closeDelay]);

  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);
  useEffect(() => {
    onOpenChangeRef.current?.(open);
  }, [open]);

  // Escape closes; a click outside closes the click-triggered folder
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (pressRef.current?.active) {
        endPress();
        return;
      }
      setOpen(false);
      setDismissed(true);
    };
    const onDown = (e: PointerEvent) => {
      if (trigger === 'click' && !rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [open, trigger]);

  const handlePointerEnter = (e: React.PointerEvent) => {
    if (e.pointerType === 'touch') return;
    setHovered(true);
  };
  const handlePointerLeave = () => {
    setHovered(false);
    setDismissed(false);
    pointerX.set(0);
    pointerY.set(0);
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!physics || reduced || e.pointerType === 'touch') return;
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    pointerX.set(clampUnit(((e.clientX - rect.left) / rect.width) * 2 - 1));
    pointerY.set(clampUnit(((e.clientY - rect.top) / rect.height) * 2 - 1));
  };
  const handleClick = () => {
    if (trigger === 'click' && !disabled) setOpen((o) => !o);
  };

  /** Drops the press and the ghost, and gives the pointer back. */
  function endPress() {
    const press = pressRef.current;
    pressRef.current = null;
    if (press?.el.hasPointerCapture(press.pointerId)) press.el.releasePointerCapture(press.pointerId);
    setGhost(null);
  }

  /** The pointer is over the folder or its papers: letting go here cancels. */
  function overFolder(clientX: number, clientY: number): boolean {
    const root = rootRef.current;
    if (!root) return true;
    const rect = root.getBoundingClientRect();
    if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) return true;
    const hit = document.elementFromPoint(clientX, clientY);
    return !!hit && root.contains(hit);
  }

  const handlePaperPointerDown = (index: number, e: React.PointerEvent<HTMLElement>) => {
    if (!onDragOut || !open || e.button !== 0 || e.pointerType === 'touch') return;
    const entry = items[index];
    if (!entry) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    pressRef.current = {
      key: entry.key,
      index,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      el: e.currentTarget,
      active: false,
      offsetX: 0,
      offsetY: 0,
    };
  };

  const handlePaperPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const press = pressRef.current;
    if (!press || press.pointerId !== e.pointerId) return;
    if (!press.active) {
      if (Math.hypot(e.clientX - press.startX, e.clientY - press.startY) < DRAG_THRESHOLD) return;
      const rootRect = rootRef.current?.getBoundingClientRect();
      const scale = rootRect && rootRect.width > 0 ? rootRect.width / width : 1;
      // Keep the paper where it was grabbed: same offset from its centre
      const rect = press.el.getBoundingClientRect();
      press.offsetX = press.startX - (rect.left + rect.width / 2) + (paperWidth * scale) / 2;
      press.offsetY = press.startY - (rect.top + rect.height / 2) + (paperHeight * scale) / 2;
      press.active = true;
      setGhost({ key: press.key, scale });
    }
    ghostX.set(e.clientX - press.offsetX);
    ghostY.set(e.clientY - press.offsetY);
    ghostOpacity.set(overFolder(e.clientX, e.clientY) ? 0.55 : 1);
  };

  const handlePaperPointerUp = async (e: React.PointerEvent<HTMLElement>) => {
    const press = pressRef.current;
    if (!press || press.pointerId !== e.pointerId) return;
    if (!press.active) {
      // A click: let onClick open the item
      endPress();
      return;
    }
    suppressClickRef.current = true;
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
    const { clientX, clientY } = e;
    const folderRect = rootRef.current?.getBoundingClientRect();
    const inside = overFolder(clientX, clientY);
    endPress();
    if (inside || !folderRect || !onDragOut) return;
    await onDragOut(press.key, press.index, { clientX, clientY, folderRect });
  };

  const pick = useCallback(
    (index: number) => {
      if (suppressClickRef.current) return;
      const entry = items[index];
      if (!entry) return;
      if (closeOnSelect) {
        setOpen(false);
        setDismissed(true);
      }
      onSelect?.(entry.key, index);
    },
    [items, closeOnSelect, onSelect],
  );

  const flapRaised = open || flapOpen;
  const motionSpring: Transition = reduced
    ? STILL
    : { type: 'spring', duration: openDuration / 1000, bounce };

  const backPath = folderBackPath(width, height, bodyTop, tabWidth, r);

  return (
    <div
      ref={rootRef}
      className={`relative ${className ?? ''}`}
      style={{ width, height }}
      data-folder-open={open ? 'true' : 'false'}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerMove={handlePointerMove}
      onClick={handleClick}
    >
      {/* Back panel with the tab */}
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="absolute inset-0 overflow-visible"
        style={{ filter: 'drop-shadow(0 1px 1.5px rgba(0,0,0,0.08)) drop-shadow(0 6px 14px rgba(0,0,0,0.07))' }}
        aria-hidden="true"
      >
        <path d={backPath} fill={folderColor} />
      </svg>

      {/* A loose sheet behind the papers, so a full folder reads as full */}
      {items.length > 0 && (
        <motion.div
          aria-hidden="true"
          className="absolute"
          style={{
            left: (width - paperWidth * 1.18) / 2,
            top: paperTop - 3 * s,
            width: paperWidth * 1.18,
            height: paperHeight,
            background: paperColor,
            borderRadius: 5 * s,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          }}
          initial={false}
          animate={{ y: open ? -6 * s : 0, rotate: -1.5 }}
          transition={motionSpring}
        />
      )}

      {/* Papers */}
      {items.map((entry, index) => (
        <Paper
          key={entry.key}
          entry={entry}
          index={index}
          count={items.length}
          open={open}
          reduced={reduced}
          physics={physics}
          drift={drift}
          s={s}
          left={(width - paperWidth) / 2}
          top={paperTop}
          paperWidth={paperWidth}
          paperHeight={paperHeight}
          spread={spread}
          lift={lift}
          tilt={tilt}
          stagger={stagger}
          spring={motionSpring}
          itemColor={itemColor}
          itemTextColor={itemTextColor}
          leanX={leanX}
          leanY={leanY}
          onPick={pick}
          draggable={!!onDragOut}
          lifted={ghost?.key === entry.key}
          onPressDown={handlePaperPointerDown}
          onPressMove={handlePaperPointerMove}
          onPressUp={handlePaperPointerUp}
          onPressCancel={endPress}
        />
      ))}

      {/* Front flap: hinged at the bottom, tilts toward the viewer */}
      <motion.div
        className="absolute pointer-events-none"
        style={{
          left: 0,
          right: 0,
          top: frontTop,
          bottom: 0,
          transformOrigin: '50% 100%',
          transformPerspective: 520 * s,
          zIndex: 20,
        }}
        initial={false}
        animate={{ rotateX: -(flapRaised ? flapAngle : restAngle) }}
        transition={motionSpring}
      >
        <div
          className="absolute inset-0"
          style={{
            background: frontColor,
            borderRadius: r,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), 0 -1px 3px rgba(0,0,0,0.06)',
          }}
        />
        <div
          className="absolute flex min-w-0 flex-col"
          style={{ left: 12 * s, right: 12 * s, bottom: 10 * s, gap: 1 * s }}
        >
          <span
            className="truncate font-semibold"
            style={{ color: labelColor, fontSize: 13 * s, lineHeight: 1.25 }}
          >
            {label}
          </span>
          {sublabel && (
            <span
              className="truncate"
              style={{ color: labelColor, opacity: 0.68, fontSize: 10.5 * s, lineHeight: 1.25 }}
            >
              {sublabel}
            </span>
          )}
        </div>
      </motion.div>

      {outlineColor && (
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="pointer-events-none absolute inset-0 overflow-visible"
          style={{ zIndex: 30 }}
          aria-hidden="true"
        >
          <path d={backPath} fill="none" stroke={outlineColor} strokeWidth={2} strokeLinejoin="round" />
        </svg>
      )}

      {children && (
        <div className="pointer-events-none absolute inset-0" style={{ zIndex: 40 }}>
          {children}
        </div>
      )}

      {ghost &&
        createPortal(
          <motion.div
            aria-hidden="true"
            className="pointer-events-none fixed left-0 top-0 overflow-hidden"
            style={{
              x: ghostX,
              y: ghostY,
              opacity: ghostOpacity,
              width: paperWidth,
              height: paperHeight,
              originX: 0,
              originY: 0,
              scale: ghost.scale,
              zIndex: 10000,
              background: itemColor,
              color: itemTextColor,
              borderRadius: 6 * s,
              boxShadow: '0 4px 8px rgba(0,0,0,0.12), 0 16px 32px rgba(0,0,0,0.16)',
            }}
          >
            {items.find((i) => i.key === ghost.key)?.content}
          </motion.div>,
          document.body,
        )}
    </div>
  );
}

interface PaperProps {
  entry: FolderFloatEntry;
  index: number;
  count: number;
  open: boolean;
  reduced: boolean;
  physics: boolean;
  drift: number;
  s: number;
  left: number;
  top: number;
  paperWidth: number;
  paperHeight: number;
  spread: number;
  lift: number;
  tilt: number;
  stagger: number;
  spring: Transition;
  itemColor: string;
  itemTextColor: string;
  leanX: MotionValue<number>;
  leanY: MotionValue<number>;
  onPick: (index: number) => void;
  /** Papers can be dragged out of the folder. */
  draggable: boolean;
  /** This paper is out being dragged: its slot shows a faint placeholder. */
  lifted: boolean;
  onPressDown: (index: number, e: React.PointerEvent<HTMLElement>) => void;
  onPressMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPressUp: (e: React.PointerEvent<HTMLElement>) => void;
  onPressCancel: () => void;
}

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

function Paper({
  entry,
  index,
  count,
  open,
  reduced,
  physics,
  drift,
  s,
  left,
  top,
  paperWidth,
  paperHeight,
  spread,
  lift,
  tilt,
  stagger,
  spring,
  itemColor,
  itemTextColor,
  leanX,
  leanY,
  onPick,
  draggable,
  lifted: draggedOut,
  onPressDown,
  onPressMove,
  onPressUp,
  onPressCancel,
}: PaperProps) {
  const [hover, setHover] = useState(false);
  const u = fanUnit(index, count);
  const lean = physics && !reduced ? drift : 0;
  // Outer papers swing further than the middle one, like a hand of cards
  const driftX = useTransform(leanX, (v) => v * lean * 10 * s * (1 + Math.abs(u) * 0.5));
  const driftY = useTransform(leanY, (v) => v * lean * 5 * s);
  const driftR = useTransform(leanX, (v) => v * lean * 4);

  const pose = open
    ? fanPose(index, count, { spread, lift, tilt, paperWidth, paperHeight })
    : restPose(index, count, paperWidth);

  // Open left to right; close the other way, twice as fast
  const delay = reduced ? 0 : open ? (index * stagger) / 1000 : ((count - 1 - index) * stagger) / 2000;
  const poseTransition: Transition = reduced ? STILL : { ...spring, delay };
  const lifted = open && hover;

  return (
    <motion.div
      className="absolute"
      style={{
        left,
        top,
        width: paperWidth,
        height: paperHeight,
        x: driftX,
        y: driftY,
        rotate: driftR,
        // Tucked: newest on top. Fanned: each paper overlaps the one to its
        // left, so every paper's left edge (where its text starts) shows.
        zIndex: lifted ? 15 : open ? index + 1 : count - index,
      }}
    >
      <motion.div
        className="h-full w-full"
        initial={false}
        animate={{ x: pose.x, y: pose.y, rotate: pose.rotate, scale: open ? 1 : 0.94 }}
        transition={poseTransition}
      >
        <motion.button
          type="button"
          tabIndex={open ? 0 : -1}
          aria-label={`Open ${entry.label}`}
          aria-hidden={!open}
          title={open ? entry.label : undefined}
          className="block h-full w-full overflow-hidden border-0 p-0 text-left outline-none focus-visible:ring-2 focus-visible:ring-signal"
          style={{
            background: itemColor,
            color: itemTextColor,
            borderRadius: 6 * s,
            boxShadow: lifted
              ? '0 2px 4px rgba(0,0,0,0.10), 0 10px 22px rgba(0,0,0,0.14)'
              : '0 1px 2px rgba(0,0,0,0.08), 0 3px 8px rgba(0,0,0,0.07)',
            pointerEvents: open ? 'auto' : 'none',
            cursor: draggedOut ? 'grabbing' : draggable ? 'grab' : 'pointer',
          }}
          initial={false}
          animate={{ y: lifted ? -6 * s : 0, scale: lifted ? 1.06 : 1, opacity: draggedOut ? 0.3 : 1 }}
          transition={reduced ? STILL : { type: 'spring', ...FOLLOW_SPRING }}
          onPointerEnter={() => setHover(true)}
          onPointerLeave={() => setHover(false)}
          // Keep the canvas card from starting a drag or a double-click open
          onMouseDown={stop}
          onTouchStart={stop}
          onDoubleClick={stop}
          onPointerDown={(e) => onPressDown(index, e)}
          onPointerMove={onPressMove}
          onPointerUp={onPressUp}
          onPointerCancel={onPressCancel}
          onLostPointerCapture={(e) => {
            // Capture lost without a pointerup (window blur, element removed)
            if (e.buttons === 0) return;
            onPressCancel();
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (open) onPick(index);
          }}
        >
          {entry.content ?? (
            <span className="block p-2 text-[11px] font-medium leading-snug">{entry.label}</span>
          )}
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

/** Folder silhouette: rounded body with a tab on the left. */
function folderBackPath(w: number, h: number, bodyTop: number, tabW: number, r: number): string {
  const rt = Math.min(r, bodyTop * 0.9); // tab corner
  const k = bodyTop * 0.9; // tab shoulder
  return [
    `M 0 ${rt}`,
    `Q 0 0 ${rt} 0`,
    `L ${tabW - k} 0`,
    `C ${tabW} 0 ${tabW - k * 0.2} ${bodyTop} ${tabW + k * 0.6} ${bodyTop}`,
    `L ${w - r} ${bodyTop}`,
    `Q ${w} ${bodyTop} ${w} ${bodyTop + r}`,
    `L ${w} ${h - r}`,
    `Q ${w} ${h} ${w - r} ${h}`,
    `L ${r} ${h}`,
    `Q 0 ${h} 0 ${h - r}`,
    'Z',
  ].join(' ');
}
